// iter87.199.180 — Shared monochrome (duotone) sport icon + accent
// mapping. One place to maintain "what color/icon means this sport"
// across FirstRunTemplatePicker (onboarding) and TemplatePicker
// (existing-crew "+ New Tournament") so both stay visually consistent
// without merging their state/logic (see FirstRunTemplatePicker.jsx's
// header comment on why the two pickers intentionally don't share code).
import { Football, SoccerBall, Golf, Baseball, Basketball, Trophy, SquaresFour } from "@phosphor-icons/react";

// One understated accent per sport — replaces the old per-card
// (NFL=cyber vs CFB=blaze vs club=neon/volt) split now that cards are
// grouped under a sport section header, so the color only needs to
// say "which sport", not "which card".
export const SPORT_META = {
    football:   { label: "American Football", Icon: Football,   accent: "cyber" },
    soccer:     { label: "Soccer",             Icon: SoccerBall, accent: "volt"  },
    golf:       { label: "Golf",               Icon: Golf,       accent: "neon"  },
    baseball:   { label: "Baseball",           Icon: Baseball,   accent: "heat"  },
    // iter87.225 — NBA Playoffs.
    basketball: { label: "Basketball",         Icon: Basketball, accent: "blaze" },
    // iter87.553.2 (testing-agent cosmetic note) — Squares, its own
    // top-level sport, gets a real icon + accent instead of falling
    // back to the plain emoji the other sport tiles don't use.
    squares:    { label: "Squares",            Icon: SquaresFour, accent: "frost" },
};

const ACCENT_TILE = {
    cyber: "bg-cyber/[0.08] text-cyber border-cyber/20",
    volt:  "bg-volt/[0.08] text-volt border-volt/20",
    neon:  "bg-neon/[0.08] text-neon border-neon/20",
    blaze: "bg-blaze/[0.08] text-blaze border-blaze/20",
    heat:  "bg-heat/[0.08] text-heat border-heat/20",
    frost: "bg-frost/[0.08] text-frost border-frost/20",
};

// Static literal class lookup — Tailwind JIT can't resolve `text-${var}`
// template strings, so any caller needing just the text color (e.g. the
// sport-tile row in TemplatePicker.jsx) uses this instead.
export const SPORT_TEXT_CLASS = {
    cyber: "text-cyber", volt: "text-volt", neon: "text-neon",
    blaze: "text-blaze", heat: "text-heat", frost: "text-frost",
};

/** Icon tile for a card row — rounded square, duotone icon, sport accent. */
export function SportIconTile({ sport, className = "" }) {
    const meta = SPORT_META[sport];
    const Icon = meta?.Icon || Trophy;
    const accent = meta?.accent || "cyber";
    return (
        <div className={`w-11 h-11 rounded-xl border flex items-center justify-center shrink-0 ${ACCENT_TILE[accent]} ${className}`}>
            <Icon size={22} weight="duotone" />
        </div>
    );
}

/** Small inline icon for section headers (no tile background). */
export function SportHeaderIcon({ sport, className = "w-3.5 h-3.5" }) {
    const meta = SPORT_META[sport];
    const Icon = meta?.Icon || Trophy;
    return <Icon className={className} weight="duotone" />;
}
